/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState } from "react";
import { Box, Button, FlexContainer } from "@nordcloud/gnui";
import { SortSelector, SortDirection } from "~/components";
import { useCurrency } from "~/hooks";
import { reverse, isEmpty } from "~/tools";
import { OrgUnitApplication } from "../../types";
import { List } from "../List";
import { ApplicationItem } from "./ApplicationItem";
import { AttachEnvs } from "./AttachEnvs/AttachEnvs";
import { SELECT_OPTIONS, SORT_FILTERS } from "./constants";
import { mapApplications, getApplications } from "./utils";

type Props = {
  applications: OrgUnitApplication[];
  refetch: () => Promise<unknown>;
  orgUnitId: string;
};

export function ApplicationList({ applications, refetch, orgUnitId }: Props) {
  const [showAttachmentSidebar, setShowAttachmentSidebar] = useState(false);

  const {
    sortedApplications,
    sortDirection,
    setSortDirection,
    activeFilter,
    setActiveFilter,
  } = useSortedApplications(applications);

  return (
    <>
      <List
        isEmpty={isEmpty(sortedApplications)}
        renderMessageContent={{
          emptyMessage:
            "The organizational unit does not have any application environments attached",
          createButton: (
            <Button
              color="notification"
              icon="link"
              onClick={() => setShowAttachmentSidebar(true)}
            >
              Attach environments
            </Button>
          ),
        }}
      >
        <Box boxStyle="lightGrey" spacing="spacing04">
          <FlexContainer justifyContent="space-between">
            <SortSelector
              options={SELECT_OPTIONS}
              value={activeFilter}
              direction={sortDirection}
              onSelect={(filter) => setActiveFilter(filter as SORT_FILTERS)}
              onSortChange={setSortDirection}
            />
            <Button
              severity="low"
              icon="link"
              onClick={() => setShowAttachmentSidebar(true)}
            >
              Attach environments
            </Button>
          </FlexContainer>
        </Box>
        {sortedApplications.map((application) => (
          <ApplicationItem
            key={application.id}
            application={application}
            orgUnitId={orgUnitId}
            onRemoveEnvFromOrgUnit={refetch}
          />
        ))}
      </List>
      <AttachEnvs
        isOpen={showAttachmentSidebar}
        onClose={() => setShowAttachmentSidebar(false)}
        onChange={refetch}
      />
    </>
  );
}

function useSortedApplications(applications: OrgUnitApplication[]) {
  const [activeFilter, setActiveFilter] = useState<SORT_FILTERS>(
    SORT_FILTERS.app_name
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.Ascending
  );
  const { currency } = useCurrency();

  const mappedApplications = mapApplications(applications, currency);

  const filteredApplications = getApplications(
    mappedApplications,
    activeFilter
  );
  const sortedApplications =
    sortDirection === SortDirection.Descending
      ? reverse(filteredApplications)
      : filteredApplications;

  return {
    sortedApplications,
    activeFilter,
    setActiveFilter,
    sortDirection,
    setSortDirection,
  };
}
